import { forwardRef, ReactNode, useImperativeHandle, useRef } from "react";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import { ErrorT } from "shared/types/common";
import { EnhancedFileItem, FileItemT, FilesListT } from "shared/types/media";
import { UploadType } from "shared/types/common";

import { TYPE_PHOTO } from "shared/constants";
import {
  MEDIA_UPLOAD_MAX_VIDEO_HEIGHT,
  MEDIA_UPLOAD_MAX_VIDEO_WIDTH,
} from "shared/constants/files";

import { uploadFileValidator } from "shared/helpers/media";

import styles from "@platformAssets/components/FilesUploader.module.scss";

import { ReactComponent as IconAddPhoto } from "assets/shared/img/ico_add_photo.svg";
import { ReactComponent as PaperclipIcon } from "assets/shared/img/icons/paperclip.svg";

type Props = {
  attachIcon?: boolean;
  showIcon?: boolean;
  identity: string;
  fileItems: Record<string, { files: FileItemT[] | EnhancedFileItem[] }>;
  acceptFiles: string[];
  acceptExtension: string[];
  maxFiles: number;
  showFileError: (args: { show: boolean; text: string | null }) => void;
  customClass?: string | null;
  label?: string | null;
  multiple?: boolean;
  isFileUploadModal?: boolean;
  addFileItems: (args: FilesListT) => void;
  onClickUpload?: () => void;
  customIcon?: ReactNode;
};

const FilesUploader = forwardRef<{ handleClickUpload: () => void }, Props>(
  (
    {
      attachIcon = false,
      showIcon = true,
      identity,
      fileItems,
      acceptFiles,
      acceptExtension,
      maxFiles,
      showFileError,
      customClass = null,
      label = null,
      multiple = true,
      isFileUploadModal = false,
      addFileItems,
      onClickUpload,
      customIcon = null,
    },
    ref
  ) => {
    const { t } = useTranslation("filesUploader");
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    useImperativeHandle(ref, () => ({
      handleClickUpload,
    }));

    /**
     * Show upload file window
     */
    const handleClickUpload = () => {
      fileInputRef.current?.click();

      onClickUpload?.();
    };

    const handleFileInputChange = (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      const { files } = event.target;
      const countSelected = files?.length;
      const filesData: FileItemT[] = [];
      const channelFiles = fileItems[identity]?.files
        ? fileItems[identity].files
        : [];
      let countFiles = channelFiles.length;
      let processedFiles = 0;
      const error: ErrorT = {
        status: false,
        message: null,
      };

      if (!countSelected) {
        finishFileSelect(filesData);
        return;
      }

      for (const file of files) {
        if (countFiles >= maxFiles) {
          error.status = true;
          error.message = t("error.max", {
            count: maxFiles,
          });
          finishFileSelect(filesData);
          break;
        }

        const temporaryUid = `file_${countFiles.toString()}_${Math.floor(
          Math.random() * (99 - 1) + 1
        )}`;
        const { status, message, fileType } = uploadFileValidator(
          file as unknown as { type: UploadType } & Omit<File, "type">,
          acceptExtension
        );

        const temporaryFile: FileItemT = {
          uid: temporaryUid,
          file,
          type: fileType!,
          uploaded: false,
          send: false,
          error: {
            status: !status,
            message,
          },
        };

        if (!status) {
          if (!error.status) {
            error.status = true;
            error.message = message;
          }

          filesData.push(temporaryFile);
          countFiles++;
          processedFiles++;
          continue;
        }

        if (fileType === TYPE_PHOTO) {
          filesData.push(temporaryFile);
          countFiles++;
          processedFiles++;
          continue;
        }

        /**
         * Can't get avi format metadata that's why ignore load data
         */
        if (file?.type === "video/avi") {
          filesData.push(temporaryFile);
          countFiles++;
          processedFiles++;
          continue;
        }

        const video = document.createElement("VIDEO") as HTMLVideoElement;
        video.src = URL.createObjectURL(file);
        filesData.push(temporaryFile);

        /**
         * Success upload of video metadata
         */
        video.addEventListener("loadedmetadata", () => {
          if (
            video.videoWidth > MEDIA_UPLOAD_MAX_VIDEO_WIDTH ||
            video.videoHeight > MEDIA_UPLOAD_MAX_VIDEO_HEIGHT
          ) {
            temporaryFile.error.status = true;
            temporaryFile.error.message = t("error.video.prop", {
              videoWidth: MEDIA_UPLOAD_MAX_VIDEO_WIDTH.toString(),
              videoHeight: MEDIA_UPLOAD_MAX_VIDEO_HEIGHT.toString(),
            });
            let iteration = 0;
            for (const channelFile of channelFiles) {
              if (temporaryFile.uid === channelFile.uid) {
                channelFiles[iteration] = temporaryFile;
                filesData.push(temporaryFile);

                if (!error.status) {
                  error.status = true;
                  error.message = temporaryFile.error.message;
                }
                break;
              }

              iteration++;
            }
          }

          if (processedFiles + 1 === countSelected) {
            finishFileSelect(filesData);

            if (error.status) {
              showFileError({ show: true, text: error.message });
            }
          }

          processedFiles++;
        });

        /**
         * If upload video metadata failed, then we show file error
         */
        video.addEventListener("error", () => {
          temporaryFile.error.status = true;
          temporaryFile.error.message = t("error.file");
          error.status = true;
          error.message = temporaryFile.error.message;

          if (processedFiles + 1 === countSelected) {
            finishFileSelect(filesData);

            if (error.status) {
              showFileError({ show: true, text: error.message });
            }
          }

          processedFiles++;
        });

        countFiles++;
      }

      if (processedFiles === countSelected) {
        finishFileSelect(filesData);
      }

      if (error.status) {
        showFileError({ show: true, text: error.message });
      }

      event.target.value = "";
    };

    /**
     * Finish files select action
     * @param {Array} files List of selected files
     */
    const finishFileSelect = (files: FileItemT[]) => {
      addFileItems({ identity, files });
    };

    return (
      <div
        className={cn(styles.ActionsFile, customClass, {
          [styles.fileUploadModal]: isFileUploadModal,
          selected: fileItems[identity]?.files?.length,
        })}
        onClick={handleClickUpload}
      >
        <span className={styles.icon}>
          {showIcon && customIcon}
          {showIcon &&
            !customIcon &&
            (attachIcon ? <PaperclipIcon /> : <IconAddPhoto />)}
        </span>
        <input
          type="file"
          multiple={multiple}
          ref={fileInputRef}
          onChange={handleFileInputChange}
          accept={acceptFiles.join(",")}
        />
        {label}
      </div>
    );
  }
);

FilesUploader.displayName = "FilesUploader";

export default FilesUploader;
